/* eslint-disable */

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";

const AGREEMENTS_COLLECTION = "customerAgreements";

function makeId(prefix) {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function serialisable(value) {
  if (!value) return value;
  if (typeof value.toDate === "function") return value.toDate().toISOString();
  if (Array.isArray(value)) return value.map(serialisable);
  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, serialisable(item)])
    );
  }
  return value;
}

function normaliseAgreement(snapshot) {
  const data = serialisable(snapshot.data() || {});
  return {
    id: snapshot.id,
    schoolName: data.schoolName || "Unnamed customer",
    customerSince: data.customerSince || "Not recorded",
    accountManager: data.accountManager || "Unassigned",
    primaryContact: data.primaryContact || { name: "", email: "" },
    agreement: {
      contractNumber: "",
      status: "Awaiting Signature",
      signedDate: "",
      startDate: "",
      endDate: "",
      term: "",
      noticePeriod: "",
      autoRenewal: false,
      ...(data.agreement || {}),
    },
    subscription: {
      plan: "Starter",
      billingCycle: "Monthly",
      monthlyValue: 0,
      studentsIncluded: 0,
      currentStudents: 0,
      storage: "0 GB",
      apiAccess: false,
      smsCredits: 0,
      ...(data.subscription || {}),
    },
    licence: {
      modules: Array.isArray(data.licence?.modules) ? data.licence.modules : [],
    },
    documents: Array.isArray(data.documents) ? data.documents : [],
    billing: {
      nextInvoice: "Not scheduled",
      lastPayment: "Not recorded",
      outstanding: 0,
      annualValue: 0,
      lifetimeValue: 0,
      ...(data.billing || {}),
    },
    compliance: {
      gdpr: false,
      dpa: false,
      privacy: false,
      safeguarding: false,
      securityReview: false,
      lastReview: "Not reviewed",
      ...(data.compliance || {}),
    },
    timeline: Array.isArray(data.timeline) ? data.timeline : [],
    notes: Array.isArray(data.notes) ? data.notes : [],
    tasks: Array.isArray(data.tasks) ? data.tasks : [],
    ...data,
    id: snapshot.id,
  };
}

async function readAgreement(agreementId) {
  const snapshot = await getDoc(doc(db, AGREEMENTS_COLLECTION, agreementId));
  if (!snapshot.exists()) throw new Error("The agreement could not be found.");
  return normaliseAgreement(snapshot);
}

export async function createAgreementInvite({
  schoolName,
  recipientName,
  recipientEmail,
  plan = "pro",
  primaryColor = "#4F46E5",
  agreementVersion = "presentplus-school-agreement-v1",
}) {
  const callable = httpsCallable(functions, "createAgreementInvite");
  const result = await callable({
    schoolName,
    recipientName,
    recipientEmail,
    plan,
    primaryColor,
    agreementVersion,
  });
  return result.data;
}

export async function deleteAgreementInvite(inviteId) {
  if (!inviteId) throw new Error("The agreement invitation ID is missing.");
  await deleteDoc(doc(db, "agreementInvites", inviteId));
}

export async function getAgreements() {
  try {
    const snapshot = await getDocs(
      query(collection(db, AGREEMENTS_COLLECTION), orderBy("schoolName", "asc"))
    );
    return snapshot.docs.map(normaliseAgreement);
  } catch (error) {
    // The orderBy query can fail while an index/rule deployment is still propagating.
    const snapshot = await getDocs(collection(db, AGREEMENTS_COLLECTION));
    return snapshot.docs.map(normaliseAgreement).sort((a, b) =>
      a.schoolName.localeCompare(b.schoolName)
    );
  }
}

export async function getAgreement(agreementId) {
  if (!agreementId) throw new Error("The agreement ID is missing.");
  return readAgreement(agreementId);
}

export async function saveAgreement(agreement) {
  if (!agreement?.id) throw new Error("The agreement ID is missing.");
  const { id, createdAt, updatedAt, ...payload } = agreement;
  await setDoc(
    doc(db, AGREEMENTS_COLLECTION, id),
    {
      ...payload,
      updatedAt: serverTimestamp(),
      ...(createdAt ? {} : { createdAt: serverTimestamp() }),
    },
    { merge: true }
  );
  return readAgreement(id);
}

export async function createAgreementRecord(agreement) {
  const id = agreement?.id || makeId("agreement");
  return saveAgreement({
    schoolName: "Unnamed customer",
    customerSince: new Date().toLocaleDateString("en-GB", {
      month: "long",
      year: "numeric",
    }),
    accountManager: "Unassigned",
    notes: [],
    tasks: [],
    timeline: [],
    documents: [],
    ...agreement,
    id,
  });
}

export async function addAgreementNote(agreementId, text, author = "CoreLogic Enterprise") {
  if (!text?.trim()) throw new Error("Enter a note before saving.");
  const ref = doc(db, AGREEMENTS_COLLECTION, agreementId);
  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(ref);
    if (!snapshot.exists()) throw new Error("The agreement could not be found.");
    const current = snapshot.data();
    transaction.update(ref, {
      notes: [
        ...(Array.isArray(current.notes) ? current.notes : []),
        {
          id: makeId("note"),
          text: text.trim(),
          author,
          date: new Date().toISOString(),
        },
      ],
      updatedAt: serverTimestamp(),
    });
  });
  return readAgreement(agreementId);
}

export async function addAgreementTask(agreementId, task) {
  if (!task?.title?.trim()) throw new Error("Enter a task title.");
  const ref = doc(db, AGREEMENTS_COLLECTION, agreementId);
  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(ref);
    if (!snapshot.exists()) throw new Error("The agreement could not be found.");
    const current = snapshot.data();
    transaction.update(ref, {
      tasks: [
        ...(Array.isArray(current.tasks) ? current.tasks : []),
        {
          id: makeId("task"),
          title: task.title.trim(),
          dueDate: task.dueDate || "",
          completed: false,
        },
      ],
      updatedAt: serverTimestamp(),
    });
  });
  return readAgreement(agreementId);
}

export async function toggleAgreementTask(agreementId, taskId) {
  const ref = doc(db, AGREEMENTS_COLLECTION, agreementId);
  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(ref);
    if (!snapshot.exists()) throw new Error("The agreement could not be found.");
    const current = snapshot.data();
    transaction.update(ref, {
      tasks: (Array.isArray(current.tasks) ? current.tasks : []).map((task) =>
        task.id === taskId ? { ...task, completed: !task.completed } : task
      ),
      updatedAt: serverTimestamp(),
    });
  });
  return readAgreement(agreementId);
}

export async function addTimelineEvent(agreementId, event) {
  const ref = doc(db, AGREEMENTS_COLLECTION, agreementId);
  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(ref);
    if (!snapshot.exists()) throw new Error("The agreement could not be found.");
    const current = snapshot.data();
    transaction.update(ref, {
      timeline: [
        ...(Array.isArray(current.timeline) ? current.timeline : []),
        {
          id: makeId("event"),
          title: event.title,
          detail: event.detail || "",
          type: event.type || "general",
          date: new Date().toISOString(),
        },
      ],
      updatedAt: serverTimestamp(),
    });
  });
  return readAgreement(agreementId);
}
